import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const ORIGIN = 'https://tradesutrust.co.uk';
const OG_IMAGE = `${ORIGIN}/TUT.png`;

const ROUTE_META = {
  '/find-tradesman': {
    title: 'Find Trusted Tradespeople Near You | TradesUTrust',
    description:
      'Search verified local electricians, plumbers, builders and more. Compare profiles and hire with confidence.',
    index: true,
  },
  '/how-it-works': {
    title: 'How TradesUTrust Works | Post Jobs and Hire Trades',
    description:
      'Learn how to post a job, compare responses from trusted tradespeople, and choose the right professional faster.',
    index: true,
  },
  '/privacy': {
    title: 'Privacy Policy | TradesUTrust',
    description: 'Read the TradesUTrust privacy policy and how we handle your personal data.',
    index: true,
  },
  '/cookie': {
    title: 'Cookie Policy | TradesUTrust',
    description: 'Read the TradesUTrust cookie policy and manage your cookie preferences.',
    index: true,
  },
  '/terms-and-conditions': {
    title: 'Terms and Conditions | TradesUTrust',
    description: 'Read the TradesUTrust terms and conditions for customers and tradespeople.',
    index: true,
  },
  '/tradesignup': {
    title: 'Tradesperson Signup | Join TradesUTrust',
    description:
      'Join TradesUTrust to receive local job opportunities and grow your trade business across the UK.',
    index: true,
  },
};

const NOINDEX_PREFIXES = [
  '/login',
  '/signup',
  '/fast-signup',
  '/post-job',
  '/payment',
  '/manage-subscription',
  '/stripe/',
  '/dashboard',
  '/chatroom',
  '/clientdetailspage',
  '/reset-password',
  '/cert-minor',
  '/eic-cert',
  '/eicr-cert',
  '/saved-certs',
  '/resources',
  '/customer-homepage',
  '/tradesman-homepage',
  '/view-interest',
  '/review',
];

function getMeta(pathname) {
  if (pathname === '/' || pathname.startsWith('/services/') || pathname.startsWith('/join/')) {
    return null;
  }

  if (pathname.startsWith('/tradesman/')) {
    return {
      title: 'Tradesperson Profile | TradesUTrust',
      description: 'View trade profile details, ratings, and services on TradesUTrust.',
      index: true,
    };
  }

  if (pathname.startsWith('/job-details/')) {
    return {
      title: 'Job Details | TradesUTrust',
      description: 'View job details and requirements.',
      index: false,
    };
  }

  if (NOINDEX_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return {
      title: 'TradesUTrust',
      description: 'TradesUTrust platform page.',
      index: false,
    };
  }

  return (
    ROUTE_META[pathname] || {
      title: 'TradesUTrust | Trusted Tradespeople Across the UK',
      description:
        'Find and hire trusted local tradespeople across the UK. Post a job and compare responses quickly.',
      index: true,
    }
  );
}

export default function RouteSeo() {
  const { pathname } = useLocation();
  const meta = getMeta(pathname);

  if (!meta) return null;

  const canonical = `${ORIGIN}${pathname === '/' ? '' : pathname}`;
  const robots = meta.index
    ? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
    : 'noindex,nofollow';

  const websiteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'TradesUTrust',
    url: ORIGIN,
  };
  const webPageLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: meta.title,
    description: meta.description,
    url: canonical,
    inLanguage: 'en-GB',
    isPartOf: {
      '@type': 'WebSite',
      url: ORIGIN,
      name: 'TradesUTrust',
    },
  };

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en-gb" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="TradesUTrust" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={OG_IMAGE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={OG_IMAGE} />

      <script type="application/ld+json">{JSON.stringify(websiteLd)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageLd)}</script>
    </Helmet>
  );
}
